import React, { useContext, useReducer, useCallback, useEffect } from 'react';
import { useApi } from '@cmr/react-api-hook';
import actions from './actions';
import { IQuestionnairesContext } from './types';
import reducer, { initSearchState } from './reducers';
import { IQuestionnaire } from '@cmr/inflow-js-common';
import { useSignalR } from '../signalr/context';

const endpoint = `${process.env['REACT_APP_API_ENDPOINT']}Questionnaires`;
export const QuestionnairesContext = React.createContext<IQuestionnairesContext | null>(
  null,
);
interface SearchProviderOptions {
  children: React.ReactElement;
}

export const useQuestionnaires = () => useContext(QuestionnairesContext)!;

export const QuestionnairesProvider = ({ children }: SearchProviderOptions) => {
  const [state, dispatch] = useReducer(reducer, initSearchState);
  const { apiFetch, authReady } = useApi();
  const { connection } = useSignalR();

  useEffect(() => {
    connection?.on(
      'signalR/QuestionnaireStatusChange',
      (payload: IQuestionnaire) => {
        dispatch(actions.questionnaireStatusChange(payload));
      },
    );
  }, [connection]);

  const fetch = useCallback(() => apiFetch(endpoint, dispatch, actions.fetch), [
    apiFetch,
  ]);

  const getRevision = useCallback(
    (name: string, revisionNumber: string | number) =>
      apiFetch(
        `${process.env['REACT_APP_API_ENDPOINT']}Questionnaire/${name}/${revisionNumber}`,
      ),
    [apiFetch],
  );

  useEffect(() => {
    const asyncWork = async () => {
      if (
        !state.isFetching &&
        !state.hasFetched &&
        !state.fetchError &&
        authReady
      )
        fetch();
    };

    asyncWork();
  }, [fetch, state.fetchError, state.hasFetched, state.isFetching, authReady]);

  return (
    <QuestionnairesContext.Provider
      value={{
        ...state,
        fetch,
        getRevision,
      }}
    >
      {children}
    </QuestionnairesContext.Provider>
  );
};
